const defaultLocalApiUrl = 'http://localhost:3000/api';
const productionApiUrl =
  'https://el-cachuchon-backend-live-production.up.railway.app/api';

function resolveApiBase() {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }

  if (typeof window === 'undefined') {
    return defaultLocalApiUrl;
  }

  const hostname = window.location.hostname;

  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return defaultLocalApiUrl;
  }

  if (hostname === 'admin.elcachuchon.com') {
    return productionApiUrl;
  }

  return `${window.location.origin}/api`;
}

const API_BASE = resolveApiBase();

export const ADMIN_TOKEN_KEY = 'ec_admin_token';

export { API_BASE };

export function getAuthToken() {
  if (typeof window === 'undefined') {
    return null;
  }

  return (
    localStorage.getItem(ADMIN_TOKEN_KEY) ||
    sessionStorage.getItem(ADMIN_TOKEN_KEY)
  );
}

export function setAuthToken(token) {
  if (!token) {
    return;
  }

  localStorage.setItem(ADMIN_TOKEN_KEY, token);
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function clearAuthToken() {
  localStorage.removeItem(ADMIN_TOKEN_KEY);
  sessionStorage.removeItem(ADMIN_TOKEN_KEY);
}

export function resolveAssetUrl(path) {
  if (!path) {
    return '';
  }

  if (path.startsWith('http://') || path.startsWith('https://')) {
    return path;
  }

  const origin = API_BASE.replace(/\/api\/?$/, '');

  return `${origin}${path.startsWith('/') ? path : `/${path}`}`;
}

async function parseJsonBody(response) {
  const text = await response.text();

  if (!text.trim()) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    if (response.ok) {
      console.warn('JSON parse failed on successful response');
      return null;
    }

    throw new Error('Invalid server response');
  }
}

function resolveToken(tokenOverride) {
  return tokenOverride || getAuthToken();
}

export function normalizeListResponse(payload) {
  if (Array.isArray(payload)) {
    return payload;
  }

  if (Array.isArray(payload?.data)) {
    return payload.data;
  }

  if (Array.isArray(payload?.products)) {
    return payload.products;
  }

  return [];
}

async function request(path, options = {}, tokenOverride) {
  const token = resolveToken(tokenOverride);

  let response;

  try {
    response = await fetch(`${API_BASE}${path}`, {
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...(options.headers || {})
      },
      ...options
    });
  } catch (error) {
    throw new Error('Network error — could not reach the server');
  }

  const payload = await parseJsonBody(response);

  if (!response.ok) {
    throw new Error(payload?.error || `Request failed (${response.status})`);
  }

  return payload;
}

export const api = {
  get: (path, token) => request(path, { method: 'GET' }, token),

  post: (path, body, token) =>
    request(path, {
      method: 'POST',
      body: JSON.stringify(body)
    }, token),

  put: (path, body, token) =>
    request(path, {
      method: 'PUT',
      body: JSON.stringify(body)
    }, token),

  patch: (path, body, token) =>
    request(path, {
      method: 'PATCH',
      body: JSON.stringify(body)
    }, token),

  delete: (path, token) => request(path, { method: 'DELETE' }, token)
};

export default api;
